import { useLazyQuery } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useCallback, useMemo, useState } from 'react';
import { useUrlSearchParams } from 'use-url-search-params';

import { useAnalytics } from '../../context/AnalyticsProvider';
import type { Currency } from '../../entities/Currency';
import type { PaymentMethodType } from '../../entities/enums';
import { PAYMENT_2C2P_INVOICE_INQUIRY } from '../../graphql/Queries';
import translations from '../../locales.vocab';
import {
  PaymentDefaultError,
  PaymentGeneralError,
} from '../Errors/customErrors';

interface use2c2pCheckStatus {
  currency: Currency;
  paymentMethod: PaymentMethodType;
  onComplete: ({ status, error }: { status?: string; error?: any }) => void;
}

const useCheckStatus = ({
  currency,
  paymentMethod,
  onComplete,
}: use2c2pCheckStatus) => {
  const { t } = useTranslations(translations);

  const [params, setParams] = useUrlSearchParams();

  const { sendLinkPaymentProcessingEvent } = useAnalytics();

  const [isCheckPaymentInProgress, setIsCheckPaymentInProgress] =
    useState(false);

  // We use this query for both invoice and caja
  const [getPaymentInquiry] = useLazyQuery(PAYMENT_2C2P_INVOICE_INQUIRY, {
    fetchPolicy: 'no-cache',
  });

  // Remove payments-widget added params
  const removeAllParams = useCallback(() => {
    const finalParam: any = {};
    Object.keys(params).forEach((k) => {
      // only remove payments-widget added params for 2c2p redirect purpose
      if (
        k.includes('payments_return') ||
        k.includes('cartid') ||
        k.includes('orderid')
      ) {
        finalParam[k] = undefined;
      } else {
        finalParam[k] = params[k];
      }
    });
    setParams(finalParam);
  }, [params, setParams]);

  const paymentCurrency = useMemo(() => currency, [currency]);

  const checkStatus = async (
    resolve: (value: string) => void,
    reject: (value: any) => void,
  ) => {
    setIsCheckPaymentInProgress(true);
    const processingPaymentData = localStorage.getItem('processingPaymentData');

    if (!processingPaymentData) {
      reject(new PaymentGeneralError(t('payment.cancel.error')));
      return;
    }

    let paymentToken;
    try {
      const data = JSON.parse(processingPaymentData);
      paymentToken = data.paymentToken;
    } catch {
      reject(new PaymentGeneralError(t('payment.cancel.error')));
      return;
    }

    try {
      const response = await getPaymentInquiry({
        variables: {
          paymentToken,
          currency: paymentCurrency,
        },
      });

      const paymentInquiryResponse =
        response.data?.payment2c2pInquiryForInvoices;
      const status = paymentInquiryResponse?.status;
      let startTime = Number(localStorage.getItem('paymentProcessStartTime'));
      if (!startTime) startTime = 0;
      const endTime = performance.now();
      setIsCheckPaymentInProgress(false);
      switch (status) {
        case 'succeeded':
          sendLinkPaymentProcessingEvent(paymentMethod, endTime - startTime);
          localStorage.removeItem('paymentProcessStartTime');
          removeAllParams();
          resolve(status);
          return;
        case 'pending':
        case 'processing':
          sendLinkPaymentProcessingEvent(paymentMethod, endTime - startTime);
          localStorage.removeItem('paymentProcessStartTime');
          removeAllParams();
          reject(new PaymentDefaultError(`Payment status is ${status}`));
          return;
        case 'cancelled':
          sendLinkPaymentProcessingEvent(paymentMethod, endTime - startTime);
          localStorage.removeItem('paymentProcessStartTime');
          removeAllParams();
          reject(new PaymentGeneralError(t('payment.cancel.error')));
          return;
        default:
          sendLinkPaymentProcessingEvent(paymentMethod, endTime - startTime);
          localStorage.removeItem('paymentProcessStartTime');
          removeAllParams();
          reject(new PaymentGeneralError(t('payment.cancel.error')));
          return;
      }
    } catch {
      setIsCheckPaymentInProgress(false);
      reject(new PaymentGeneralError(t('payment.cancel.error')));
      return;
    }
  };

  const handleCheckButtonClick = async (
    sendLinkCheckPaymentEvent: (paymentMethod: PaymentMethodType) => void,
  ) => {
    sendLinkCheckPaymentEvent(paymentMethod);
    try {
      const paymentStatus = await new Promise((resolve, reject) =>
        checkStatus(resolve, reject),
      );

      if (paymentStatus === 'succeeded') {
        localStorage.removeItem('processingPaymentData');
        onComplete({ status: paymentStatus as string });
      }
    } catch (error) {
      removeAllParams();
      onComplete({ error });
    }
  };

  return { handleCheckButtonClick, isCheckPaymentInProgress };
};

export default useCheckStatus;
